/**
 * Enhanced Error Handler for Image-to-3D Generation
 * Provides detailed error analysis and recovery strategies
 */

import logger from './vercelRuntimeLogger';

export interface GenerationError {
  type: 'NETWORK' | 'AUTH' | 'QUOTA' | 'API' | 'FIREBASE' | 'TIMEOUT' | 'UNKNOWN';
  message: string;
  details?: any;
  retryable: boolean;
  suggestedAction?: string;
}

export class GenerationErrorHandler {
  private static readonly ERROR_PATTERNS = {
    NETWORK: [
      /Failed to fetch/i,
      /NetworkError/i,
      /ERR_NETWORK/i,
      /ERR_INTERNET_DISCONNECTED/i,
      /ERR_CONNECTION_REFUSED/i,
      /ECONNREFUSED/i,
    ],
    CORS: [
      /CORS/i,
      /cross-origin/i,
      /No 'Access-Control-Allow-Origin'/i,
    ],
    AUTH: [
      /Authentication failed/i,
      /Unauthorized/i,
      /401/,
      /User not found/i,
    ],
    QUOTA: [
      /quota/i,
      /limit exceeded/i,
      /no generations left/i,
      /rate limit/i,
    ],
    TIMEOUT: [
      /timeout/i,
      /timed out/i,
      /ERR_CONNECTION_TIMED_OUT/i,
    ],
    API: [
      /500/,
      /502/,
      /503/,
      /504/,
      /Internal Server Error/i,
      /Bad Gateway/i,
      /Service Unavailable/i,
    ],
  };

  static analyzeError(error: any): GenerationError {
    const errorMessage = this.extractErrorMessage(error);
    const errorType = this.categorizeError(errorMessage, error);
    
    logger.error('ErrorHandler', 'ERROR_ANALYZED', error, {
      errorType,
      errorMessage,
      stack: error?.stack,
      response: error?.response,
    });

    return {
      type: errorType,
      message: this.getUserFriendlyMessage(errorType, errorMessage),
      details: {
        originalError: error,
        message: errorMessage,
        timestamp: new Date().toISOString(),
      },
      retryable: this.isRetryable(errorType),
      suggestedAction: this.getSuggestedAction(errorType),
    };
  }

  private static extractErrorMessage(error: any): string {
    if (typeof error === 'string') return error;
    if (error?.message) return error.message;
    if (error?.error) return this.extractErrorMessage(error.error);
    if (error?.statusText) return error.statusText;
    return 'Unknown error occurred';
  }

  private static categorizeError(message: string, error: any): GenerationError['type'] {
    // Check for specific HTTP status codes
    if (error?.status === 401 || error?.response?.status === 401) return 'AUTH';
    if (error?.status >= 500 || error?.response?.status >= 500) return 'API';

    // Check error message patterns
    for (const [type, patterns] of Object.entries(this.ERROR_PATTERNS)) {
      if (patterns.some(pattern => pattern.test(message))) {
        // Special handling for CORS errors
        if (type === 'CORS') return 'NETWORK';
        return type as GenerationError['type'];
      }
    }

    return 'UNKNOWN';
  }

  private static getUserFriendlyMessage(type: GenerationError['type'], originalMessage: string): string {
    const messages = {
      NETWORK: 'Connection failed. Please check your internet connection and try again.',
      AUTH: 'Authentication required. Please sign in and try again.',
      QUOTA: 'Generation limit reached. Please upgrade your plan or wait for the limit to reset.',
      API: 'Our servers are experiencing issues. Please try again in a few moments.',
      FIREBASE: 'Unable to track generation status. Please refresh the page.',
      TIMEOUT: 'The request took too long. The generation may still be processing - please check back in a minute.',
      UNKNOWN: `An unexpected error occurred: ${originalMessage}`,
    };

    return messages[type] || originalMessage;
  }

  private static isRetryable(type: GenerationError['type']): boolean {
    return ['NETWORK', 'API', 'TIMEOUT'].includes(type);
  }

  private static getSuggestedAction(type: GenerationError['type']): string | undefined {
    const actions: Record<GenerationError['type'], string> = {
      NETWORK: 'Check your internet connection and try again',
      AUTH: 'Sign in with your Google account',
      QUOTA: 'Upgrade to a paid plan for more generations',
      API: 'Wait a few moments and try again',
      FIREBASE: 'Refresh the page to restore the connection',
      TIMEOUT: 'Check the generation status in a minute',
      UNKNOWN: 'Please try again or contact support if the issue persists',
    };

    return actions[type];
  }
}

export class RetryHandler {
  private static readonly MAX_RETRIES = 3;
  private static readonly BASE_DELAY = 1000; // 1 second
  private static readonly MAX_DELAY = 10000; // 10 seconds

  static async withRetry<T>(
    operation: () => Promise<T>,
    options: {
      maxRetries?: number;
      onRetry?: (attempt: number, error: any) => void;
      shouldRetry?: (error: any) => boolean;
    } = {}
  ): Promise<T> {
    const maxRetries = options.maxRetries ?? this.MAX_RETRIES;
    let lastError: any;

    for (let attempt = 1; attempt <= maxRetries; attempt++) {
      try {
        return await operation();
      } catch (error) {
        lastError = error;
        
        const errorInfo = GenerationErrorHandler.analyzeError(error);

        const customShouldRetry = options.shouldRetry
          ? options.shouldRetry(error)
          : undefined;
        
        if (attempt === maxRetries) {
          throw error;
        }

        if (typeof customShouldRetry === 'boolean') {
          if (!customShouldRetry) {
            throw error;
          }
        } else if (!errorInfo.retryable) {
          throw error;
        }

        const delay = Math.min(
          this.BASE_DELAY * Math.pow(2, attempt - 1),
          this.MAX_DELAY
        );

        logger.info('RetryHandler', 'RETRY_ATTEMPT', {
          attempt,
          maxRetries,
          delay,
          errorType: errorInfo.type,
          errorMessage: errorInfo.message,
        });

        if (options.onRetry) {
          options.onRetry(attempt, error);
        }

        await new Promise(resolve => setTimeout(resolve, delay));
      }
    }

    throw lastError;
  }
}

// Network connectivity checker
export class NetworkMonitor {
  private static listeners: Set<(online: boolean) => void> = new Set();

  static initialize() {
    if (typeof window === 'undefined') return;

    window.addEventListener('online', () => this.notifyListeners(true));
    window.addEventListener('offline', () => this.notifyListeners(false));
  }

  static isOnline(): boolean {
    if (typeof window === 'undefined') return true;
    return window.navigator.onLine;
  }

  static addListener(callback: (online: boolean) => void) {
    this.listeners.add(callback);
  }

  static removeListener(callback: (online: boolean) => void) {
    this.listeners.delete(callback);
  }

  private static notifyListeners(online: boolean) {
    logger.info('NetworkMonitor', online ? 'NETWORK_ONLINE' : 'NETWORK_OFFLINE');
    this.listeners.forEach(listener => listener(online));
  }
}

// Initialize network monitor
NetworkMonitor.initialize();
